// The whole view is in the address (SPEC-redesign §3, §4.2): sources, lines,
// silence, solo, the line lens, radius, centre, time, the order, the open
// detail, the open screen. A link reproduces exactly that screen, and Back or
// Esc unwinds one step of whatever was opened.
//
// This module is the only writer of `location.hash` in the tree. The codec it
// writes through — the ten keys, their defaults and their spelling — is
// `src/state/hash.ts`; what is here is the store, the history and this
// browser's memory.

import { parseHash, toHash, type ViewState } from './hash.ts';
import { memory } from './memory.ts';
import { awayFromNow } from './moved.ts';

export type { Centre, Order, Screen, ViewState } from './hash.ts';
export { DEFAULT_VIEW, parseHash, radiusIncluding, toHash } from './hash.ts';

/** Which opening an entry we pushed stands for, so the `‹` buttons know their own entry. */
type OpenKind = 'sel' | 'ekran' | 'centre' | 'solo' | 'tylko' | 'other';
type Bookmark = {
  depth: number;
  kind: OpenKind;
  /**
   * Whether the field this entry opened was **already** open underneath it — a
   * detail tapped while another detail was open, a screen opened from a screen.
   * Recorded at push time because it cannot be recovered later: the History API
   * exposes no other entry's state, and `history.back()` is asynchronous.
   */
  wasOpen: boolean;
};

function kindOf(patch: Partial<ViewState>): OpenKind {
  if (patch.selected != null) return 'sel';
  if (patch.screen != null) return 'ekran';
  if (patch.centre != null || patch.at != null) return 'centre';
  if (patch.solo != null) return 'solo';
  if (patch.onlyLine != null) return 'tylko';
  return 'other';
}

const KEY = 'poznanTeraz';
/** What `claim()` stamps an entry with, and what an unstamped one is read as. */
const FRESH: Bookmark = { depth: 0, kind: 'other', wasOpen: false };
/** The bookmark on the entry the tab is on — null on an entry this session did not write. */
const stamped = (): Bookmark | null => {
  const state: unknown = history.state;
  const found =
    state && typeof state === 'object' && KEY in state ? (state as Record<string, unknown>)[KEY] : null;
  return found && typeof found === 'object' && 'depth' in found ? (found as Bookmark) : null;
};
const bookmark = (): Bookmark => stamped() ?? FRESH;

const listeners = new Set<(now: ViewState, before: ViewState) => void>();

// Read on first use rather than on import, so that `parseHash`, `toHash` and
// `DEFAULT_VIEW` can be pulled into a `node:test` for the pure modules that
// depend on them without standing up an address bar first.
let current: ViewState | null = null;
const now = (): ViewState => (current ??= parseHash(location.hash));

// Every field changed by a `'replace'` write since the last opening. A `'replace'`
// is by definition not an opening, so stepping back off an opening must not undo
// it: a layer switched off inside „Warstwy", a detail shut with `‹`, a radius
// dragged — all of it rides along onto the entry we land on (D-b). Cleared by the
// next opening, which starts a new stretch.
let carried: Partial<ViewState> = {};

function url(v: ViewState): string {
  const hash = toHash(v);
  return location.pathname + location.search + (hash ? `#${hash}` : '');
}

/**
 * One subscriber's mistake is its own. Once S1–S4 each subscribe, a thrower
 * would otherwise silence every subscriber behind it, and the symptom is „the
 * address changes and half the screen does not follow". A copy, because a
 * subscriber may subscribe or dispose from inside its own callback: iterating
 * the live Set would visit a newcomer with a change it was not subscribed for.
 */
function announce(before: ViewState) {
  const listening = [...listeners];
  for (const fn of listening) {
    if (!listeners.has(fn)) continue;
    try {
      // read per subscriber, never hoisted: a subscriber may write from inside
      // its own callback, and everyone after it must see what the state is now
      // rather than what it was when the notification started
      fn(now(), before);
    } catch (e) {
      console.error('viewstate: a subscriber threw on a change', e);
    }
  }
}

/**
 * What this browser remembers, written where the resident changed it and
 * nowhere else: a link someone sent must never overwrite the lines this
 * resident chose (§4.6), so `sync()` never comes through here. Only the keys in
 * `patch` are written, and that per-key guard is load-bearing rather than a
 * saving — ADR-0009, Consequences, says what writing all of them at once
 * leaked. Four keys now: `uklad` is remembered under the same rule as the
 * radius (SPEC-viewfinder §2.3).
 */
function persist(patch: Partial<ViewState>, view: ViewState) {
  if ('lines' in patch) memory.lines.set(view.lines);
  if ('muted' in patch) memory.muted.set(view.muted);
  if ('radius' in patch) memory.radius.set(view.radius);
  if ('order' in patch) memory.order.set(view.order);
}

function write(next: ViewState, mode: 'open' | 'replace') {
  const before = now();
  current = next;
  const patch = diff(before, next);
  const kind = kindOf(patch);
  const mark: Bookmark =
    mode === 'open'
      ? {
          depth: bookmark().depth + 1,
          kind,
          wasOpen: (kind === 'sel' && before.selected != null) || (kind === 'ekran' && before.screen != null),
        }
      : bookmark();
  const state = { [KEY]: mark };
  if (mode === 'open') {
    carried = {};
    history.pushState(state, '', url(next));
  } else {
    Object.assign(carried, diff(before, next));
    history.replaceState(state, '', url(next));
  }
  persist(patch, next);
  announce(before);
}

function diff(before: ViewState, after: ViewState): Partial<ViewState> {
  const patch: Partial<ViewState> = {};
  for (const key of Object.keys(after) as (keyof ViewState)[]) {
    if (JSON.stringify(before[key]) !== JSON.stringify(after[key])) {
      Object.assign(patch, { [key]: after[key] });
    }
  }
  return patch;
}

/**
 * Stamps the entry the tab is on as this session's, leaving `location.hash`
 * exactly as it stands — tidying it is `normalise()`'s job, which runs later.
 * `FRESH` is what an unstamped entry already reads as, so `back()` and `close()`
 * see what they saw before: the stamp says whose entry this is and nothing more.
 */
function claim() {
  if (stamped()) return;
  history.replaceState({ [KEY]: FRESH }, '', location.pathname + location.search + location.hash);
}

/**
 * A hash we did not write. The event cannot say where it came from: a fragment
 * navigation — an address typed into this tab, an in-page `#…` anchor, an
 * assignment to `location.hash` — fires `popstate` and then `hashchange`,
 * exactly as Back and Forward do (#278, measured). The ENTRY says it. Stepping
 * onto one this session wrote brings the carried changes along, which is what
 * makes D-b true; an arriving address landed on an entry of its own, so it wins
 * outright and starts a new stretch, as an opening would.
 */
function sync() {
  let next = parseHash(location.hash);
  const before = now();
  if (stamped()) {
    if (Object.keys(carried).length > 0) {
      next = { ...next, ...carried };
      if (toHash(next) !== location.hash.replace(/^#/, ''))
        history.replaceState(history.state, '', url(next));
    }
  } else {
    carried = {};
    claim();
  }
  if (toHash(next) === toHash(before)) return;
  current = next;
  announce(before);
}

if (typeof addEventListener === 'function') {
  // The entry the session opens on is its own, or a Back onto it would arrive
  // as a stranger and drop what was curated one screen deeper.
  claim();
  // Both events, because both reach us for the same navigation; the second call
  // finds the entry claimed and the carried changes already merged.
  addEventListener('popstate', sync);
  addEventListener('hashchange', sync);
}

/** The one opening that the local close at depth 0 unwinds, most recent first. */
function closeLocally(): Partial<ViewState> | null {
  const view = now();
  if (view.selected != null) return { selected: null };
  if (view.screen != null) return { screen: null };
  // `awayFromNow`, not `movedByHand`: `tu=ja` was an opening too (§4.2, #226).
  if (awayFromNow(view)) return { centre: { kind: 'default' }, at: null };
  if (view.solo != null) return { solo: null };
  if (view.onlyLine != null) return { onlyLine: null };
  return null;
}

export const viewState = {
  get: (): ViewState => now(),
  /** The two modes of ADR-0009: `'open'` pushes an entry, `'replace'` pushes none. */
  set(patch: Partial<ViewState>, mode: 'open' | 'replace'): void {
    const next = { ...now(), ...patch };
    // Nothing moved, so neither the address nor the history stack should.
    if (toHash(next) === toHash(now())) return;
    write(next, mode);
  },
  /** Called with the new and the previous state on every change, including `hashchange`. */
  subscribe(fn: (now: ViewState, before: ViewState) => void): () => void {
    listeners.add(fn);
    return () => {
      listeners.delete(fn);
    };
  },
  /** Unwinds one opening: Back when this session pushed an entry, else a local close. */
  back(): void {
    if (bookmark().depth > 0) {
      history.back();
      return;
    }
    // Depth 0: this session pushed nothing, so `history.back()` would walk the
    // resident out of the app. A deep link that already carries `sel` closes here.
    const patch = closeLocally();
    if (patch) write({ ...now(), ...patch }, 'replace');
  },
  /**
   * Boot-time address hygiene, once, before anything is restored: an address
   * that does not round-trip is rewritten so that it does, or the resident's
   * next copy-paste passes the junk on. No history entry, so Back and the depth
   * are unaffected, and no notification: only the spelling changed. An address
   * that is already canonical is left alone down to the `replaceState`.
   */
  normalise(): void {
    const canonical = toHash(now());
    if (canonical === location.hash.replace(/^#/, '')) return;
    history.replaceState(history.state, '', url(now()));
  },
  /**
   * The `‹` buttons. Closing must never leave an entry that Back would re-open,
   * and must never undo something opened on top of what is being closed.
   */
  closeDetail(): void {
    close('sel');
  },
  closeScreen(): void {
    close('ekran');
  },
};

function close(kind: 'sel' | 'ekran') {
  const view = now();
  const open = kind === 'sel' ? view.selected : view.screen;
  if (open == null) return;
  const mark = bookmark();
  if (mark.depth > 0 && mark.kind === kind && !mark.wasOpen) {
    // Our own entry is on top and nothing of this kind was open underneath it:
    // stepping off it both removes the field and keeps the stack from growing
    // by two on every open/close pair.
    history.back();
    return;
  }
  // Either something was opened on top of it — „Stąd, o 15:00" while the detail
  // was open — or this entry opened a second detail over a first, where
  // stepping back would land on the earlier one and re-open it. Dropping the
  // field in place keeps whatever was opened, and the `'replace'` being carried
  // is what lets the next Back undo that step without re-opening what was shut.
  write({ ...view, [kind === 'sel' ? 'selected' : 'screen']: null }, 'replace');
}
